import { Autocomplete, Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { useMemo } from "react";

import { AvailableProduct, ScheduleImportSummary } from "../../../../services/production-plan-api";
import { planner } from "../../planner-ui";
import { NameFixes, NamesToCheck, NameToCheck } from "../utils/schedule-upload"; // eslint-disable-line sort-imports

interface ScheduleImportReviewProps {
	summary: ScheduleImportSummary;
	names: NamesToCheck;
	fixes: NameFixes;
	workcenters: string[];
	/** The plant a line belongs to, when the upload covers several. */
	plantOf?: (line: string) => string | undefined;
	products: AvailableProduct[];
	onFixLine: (raw: string, workcenter: string | null) => void;
	onFixProduct: (raw: string, productId: number | null) => void;
}

const LEAVE_OUT = "__leave_out__";

const FORMAT_LABEL: Record<string, string> = {
	"planning-grid": "planning grid",
	"schedule-rows": "schedule rows",
	"shift-grid": "shift sheet (line × shift × day)",
};

const dayLabel = (iso?: string) => (iso ? format(parseISO(iso), "d MMM yyyy") : "");

const metaSx = { ...planner.type.meta, color: planner.inkMuted };

const byLabel = (by: string | null) => {
	if (by === "spelling") return "close match — check it";
	if (by === "ai") return "suggested by Claude";
	return "not matched";
};

/**
 * Above the rows of a schedule upload: what was read and skipped, then each
 * line and product name the reader was not sure of — fix it once and every
 * row with that name follows. Leaving a name out leaves its rows out.
 */
const ScheduleImportReview = ({ summary, names, fixes, workcenters, plantOf, products, onFixLine, onFixProduct }: ScheduleImportReviewProps) => {
	const lineText = (line: string) => {
		const plant = plantOf?.(line);
		return plant && !line.endsWith(` · ${plant}`) ? `${line} · ${plant}` : line;
	};
	const productOptions = useMemo(() => [...products].sort((a, b) => a.name.localeCompare(b.name)), [products]);
	const productById = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);
	const skipped = [
		summary.actualRowsSkipped > 0 && `${summary.actualRowsSkipped} actual rows`,
		summary.historyRunsSkipped > 0 && `${summary.historyRunsSkipped} runs before this week`,
		summary.earlierBatchesSkipped > 0 && `${summary.earlierBatchesSkipped} earlier batches of runs already under way`,
		summary.numberOnlyCellsSkipped > 0 && `${summary.numberOnlyCellsSkipped} number-only cells`,
	].filter(Boolean);

	const lineValue = (name: NameToCheck<string>) => {
		const value = name.raw in fixes.lines ? fixes.lines[name.raw] : name.current;
		return value || LEAVE_OUT;
	};
	const productValue = (name: NameToCheck<number>) => {
		const id = name.raw in fixes.products ? fixes.products[name.raw] : name.current;
		return id ? productById.get(id) ?? null : null;
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
			<Box
				sx={{
					p: 1.5,
					borderRadius: `${planner.radiusSm}px`,
					border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
					bgcolor: planner.surfaceSubtle,
				}}
			>
				<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
					Read as a {FORMAT_LABEL[summary.format] ?? summary.format}
					{summary.sheet ? ` · ${summary.sheet}` : ""}
					{summary.firstDay ? ` · ${dayLabel(summary.firstDay)} – ${dayLabel(summary.lastDay)}` : ""}
				</Typography>
				<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
					{summary.runsRead} runs → {summary.batchOrdersBuilt} batch orders
					{summary.blocksBuilt > 0 ? ` and ${summary.blocksBuilt} cleaning / maintenance blocks` : ""}.
					{skipped.length > 0 ? ` Skipped ${skipped.join(", ")}.` : ""}
				</Typography>
				{summary.aiUsed && (
					<Typography sx={metaSx}>
						Claude read {summary.textsAskedAi} run note{summary.textsAskedAi === 1 ? "" : "s"} and {summary.namesAskedAi} product
						name{summary.namesAskedAi === 1 ? "" : "s"} the rules could not; its matches are marked below.
					</Typography>
				)}
			</Box>

			{names.lines.length > 0 && (
				<Box>
					<Typography sx={{ ...planner.labelCaps, color: planner.inkSecondary, mb: 0.5 }}>
						Lines to check
					</Typography>
					{names.lines.map((name) => (
						<Box key={name.raw} sx={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 1, alignItems: "center", py: 0.5 }}>
							<Box>
								<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>{name.raw}</Typography>
								<Typography sx={metaSx}>
									{name.count} row{name.count === 1 ? "" : "s"} · {byLabel(name.by)}
								</Typography>
							</Box>
							<Select
								size="small"
								value={lineValue(name)}
								renderValue={(value) => (value === LEAVE_OUT ? "Leave out" : lineText(String(value)))}
								onChange={(event) => onFixLine(name.raw, event.target.value === LEAVE_OUT ? null : String(event.target.value))}
								aria-label={`Line for ${name.raw}`}
							>
								<MenuItem value={LEAVE_OUT}>Leave out</MenuItem>
								{workcenters.map((workcenter) => (
									<MenuItem key={workcenter} value={workcenter}>
										{lineText(workcenter)}
									</MenuItem>
								))}
							</Select>
						</Box>
					))}
				</Box>
			)}

			{names.products.length > 0 && (
				<Box>
					<Typography sx={{ ...planner.labelCaps, color: planner.inkSecondary, mb: 0.5 }}>
						Products to check
					</Typography>
					{names.products.map((name) => (
						<Box key={name.raw} sx={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 1, alignItems: "center", py: 0.5 }}>
							<Box>
								<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>{name.raw}</Typography>
								<Typography sx={metaSx}>
									{name.count} row{name.count === 1 ? "" : "s"} · {byLabel(name.by)}
								</Typography>
							</Box>
							<Autocomplete
								size="small"
								options={productOptions}
								value={productValue(name)}
								getOptionLabel={(option) => option.name}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								onChange={(_, value) => onFixProduct(name.raw, value?.id ?? null)}
								renderInput={(params) => <TextField {...params} placeholder="Leave out" aria-label={`Product for ${name.raw}`} />}
							/>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

export default React.memo(ScheduleImportReview);
